import { Button, Col, Modal, Row, Space, Typography, message } from "antd";
import CalendarView from "../components/CalendarView";
import ComboBox from "../controls/ComboBox";
import { InputObject } from "../controls/default";
import { useState } from "react";
import { getProblemMessage, retry } from "../utils/http";
import Loader from "../controls/Loader";
import {
  getCompanies,
  getCompanyDepartments,
  getCalendarEventTypes,
  getCalendarEventsExport,
} from "../repository/data.repository";
import { nameOf } from "../utils/typescript";
import { Company, Department } from "../repository/data.default";
import { QueryCondition, QueryOperation } from "../code/search-query/search-query";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

function CalendarPage() {
  const [modal, contextHolder] = Modal.useModal();
  const [openEvent, setOpenEvent] = useState<
    "add" | "update" | "delete" | "preview" | null
  >(null);
  const [selectedCompany, setSelectedCompany] =
    useState<InputObject<Company> | null>(null);
  const [selectedDepartment, setSelectedDepartment] =
    useState<InputObject | null>(null);
  const [selectedType, setSelectedType] = useState<InputObject | null>(null);
  const permissions = useSelector(authorizeSelectors.permissions);

  // Events

  function onCompanySelect(value?: InputObject<Company> | null): void {
    setSelectedCompany(value ?? null);
    // Department is company-scoped — reset it whenever the company changes.
    setSelectedDepartment(null);
    setSelectedType(null);
  }

  function onDepartmentSelect(value?: InputObject | null): void {
    setSelectedDepartment(value ?? null);
  }

  function onTypeSelect(value?: InputObject | null): void {
    setSelectedType(value ?? null);
  }

  function onAddEvent(): void {
    setOpenEvent("add");
  }

  // Export the currently-filtered events (company is required; the server widens companyUid to the subtree and
  // applies the caller's visibility). Department / Event Type narrow the export when selected.
  function onExport(): void {
    if (!selectedCompany?.value) return;
    const conditions: QueryCondition[] = [
      { field: "companyUid", operation: QueryOperation.Equal, values: [selectedCompany.value] },
    ];
    if (selectedDepartment?.value)
      conditions.push({ field: "departmentUid", operation: QueryOperation.Equal, values: [selectedDepartment.value] });
    if (selectedType?.value)
      conditions.push({ field: "typeUid", operation: QueryOperation.Equal, values: [selectedType.value] });
    const loading = modal.info({
      content: (
        <Typography.Text className="m-1 ml-2 inline-block">
          Downloading calendar events export file..
        </Typography.Text>
      ),
      footer: null,
      centered: true,
      icon: <Loader />,
    });
    getCalendarEventsExport({ searchQuery: { conditions }, pageNumber: 0, pageSize: 100000 })
      .then(() => loading.destroy())
      .catch((err) => {
        loading.destroy();
        message.error(getProblemMessage(err) || "Failed to download export.");
      });
  }

  function onEventClose(): void {
    setOpenEvent(null);
  }

  // Methods

  const companiesRequest = (search: string) => {
    return retry(() =>
      getCompanies({
        searchQuery: {
          conditions: [
            {
              field: nameOf<Company>("name"),
              operation: QueryOperation.StartsWith,
              values: [search],
            },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  const departmentsRequest = (search: string) => {
    const conditions: {
      field: string;
      operation: QueryOperation;
      values: string[];
    }[] = [
      {
        field: nameOf<Department>("name"),
        operation: QueryOperation.StartsWith,
        values: [search],
      },
    ];

    if (selectedCompany?.value)
      conditions.push({
        field: nameOf<Department>("companyUid"),
        operation: QueryOperation.Equal,
        values: [selectedCompany.value],
      });

    return retry(() =>
      getCompanyDepartments({
        searchQuery: { conditions },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  // Unlike the event form's type picker, this FILTER deliberately offers reserved codes too —
  // narrowing the calendar to counselling/training sessions is a legitimate view.
  const typesRequest = (search: string) => {
    return retry(() =>
      getCalendarEventTypes({
        searchQuery: {
          conditions: [
            { field: "name", operation: QueryOperation.StartsWith, values: [search] },
          ],
        },
        pageNumber: 0,
        pageSize: 100,
      }),
    ).then(({ data }) =>
      data.map((dataItem) => ({
        display: dataItem.name,
        value: dataItem.uid,
        dataItem,
      })),
    );
  };

  return (
    <Col className="flex flex-1 flex-col overflow-hidden p-4">
      <Row className="sticky top-0 z-10 flex flex-row flex-wrap items-center p-4 pl-0 pr-0">
        <Typography.Title className="m-0 flex-auto" level={4}>
          Calendar
        </Typography.Title>
        <Space className="flex flex-row">
          <ComboBox
            className="w-64"
            placeholder="Search by company"
            request={companiesRequest}
            onChange={onCompanySelect}
          />
          <ComboBox
            // Remount on company change so the previous department selection clears.
            key={`dept-${selectedCompany?.value ?? "none"}`}
            className="w-56"
            placeholder="Department (optional)"
            disabled={!selectedCompany}
            value={selectedDepartment ?? undefined}
            request={departmentsRequest}
            onChange={onDepartmentSelect}
          />
          <ComboBox
            key={`type-${selectedCompany?.value ?? "none"}`}
            className="w-52"
            placeholder="Event type (optional)"
            disabled={!selectedCompany}
            value={selectedType ?? undefined}
            request={typesRequest}
            onChange={onTypeSelect}
          />
          <Button
            onClick={onExport}
            disabled={!selectedCompany || !permissions.can("CalendarEvents", "view")}
          >
            Export to Excel
          </Button>
          <Button
            type="primary"
            onClick={onAddEvent}
            disabled={!permissions.can("CalendarEvents", "create")}

            className={`${!permissions.can("CalendarEvents", "create") ? "disabled" : ""}`}
          >
            Add Event
          </Button>
        </Space>
      </Row>
      <CalendarView
        openEvent={openEvent}
        selectedCompany={selectedCompany}
        selectedDepartment={selectedDepartment}
        selectedType={selectedType}
        onEventClose={onEventClose}
      />
      {contextHolder}
    </Col>
  );
}

export default CalendarPage;
